import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { FlowIconName } from "./landingContent";

type LandingButtonProps = {
  href: string;
  children: ReactNode;
  variant?: "primary" | "secondary";
  className?: string;
};

type LandingCardProps = {
  children: ReactNode;
  className?: string;
  glow?: boolean;
  plain?: boolean;
};

type LandingSectionProps = {
  id?: string;
  eyebrow?: string;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  titleAs?: "h1" | "h2";
};

function isInAppPath(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//") && !href.includes("#");
}

export function LandingButton({
  href,
  children,
  variant = "primary",
  className = "",
}: LandingButtonProps) {
  const classNameValue = `landing-button landing-button--${variant} ${className}`.trim();

  if (isInAppPath(href)) {
    return (
      <Link to={href} className={classNameValue}>
        <span className="landing-button__label">{children}</span>
      </Link>
    );
  }

  return (
    <a href={href} className={classNameValue}>
      <span className="landing-button__label">{children}</span>
    </a>
  );
}

export function LandingCard({
  children,
  className = "",
  glow = false,
  plain = false,
}: LandingCardProps) {
  return (
    <div
      className={`landing-card-shell ${plain ? "landing-card-shell--plain" : "landing-glass"} ${
        glow ? "landing-card-shell--glow" : ""
      } ${className}`.trim()}
    >
      <div className="landing-card-shell__content">{children}</div>
    </div>
  );
}

export function LandingSection({
  id,
  eyebrow,
  title,
  description,
  children,
  className = "",
  titleAs = "h2",
}: LandingSectionProps) {
  const TitleTag = titleAs;

  return (
    <section id={id} className={`landing-section ${className}`.trim()}>
      <div className="landing-shell">
        <div className="landing-section__head">
          {eyebrow ? <p className="landing-section__eyebrow">{eyebrow}</p> : null}
          <TitleTag>{title}</TitleTag>
          {description ? <p>{description}</p> : null}
        </div>
        {children}
      </div>
    </section>
  );
}

export function FlowIcon({
  icon,
  className,
}: {
  icon: FlowIconName;
  className?: string;
}) {
  if (icon === "pin") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={className}>
        <path d="M12 21c3.5-4.1 5.2-7.1 5.2-9.3A5.2 5.2 0 1 0 6.8 11.7C6.8 13.9 8.5 16.9 12 21Z" />
        <circle cx="12" cy="11.2" r="1.9" />
      </svg>
    );
  }

  if (icon === "fuel") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={className}>
        <path d="M6 20V6.8A1.8 1.8 0 0 1 7.8 5h6.4A1.8 1.8 0 0 1 16 6.8V20" />
        <path d="M6 20h10" />
        <path d="M16 9h2.4l1.6 1.8v5.4a1.8 1.8 0 0 1-3.6 0V13" />
        <path d="M9 8.4h4" />
      </svg>
    );
  }

  if (icon === "document") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={className}>
        <path d="M8 3.8h6.6L19 8.2V19a1.8 1.8 0 0 1-1.8 1.8H8A1.8 1.8 0 0 1 6.2 19V5.6A1.8 1.8 0 0 1 8 3.8Z" />
        <path d="M14.6 3.8v4.4H19" />
        <path d="M9.4 12.2h5.2" />
        <path d="M9.4 15.6h5.2" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={className}>
      <path d="M21 12 3 4.5l3.1 6.2L3 17.5 21 12Z" />
      <path d="M6 10.7h6.2" />
    </svg>
  );
}
